import React, { useState, useRef, useEffect } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { Lang } from '../../i18n';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../hooks/useNotifications';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { NotificationDrawer } from './NotificationDrawer';

export const CabinetHeader: React.FC = () => {
  const { lang, setLang, t } = useI18n();
  const { logout, isAuthenticated } = useAuth();
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  
  const [isPWA, setIsPWA] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { notifications, unreadCount, markAsRead } = useNotifications();
  const { isSupported, isSubscribed, subscribe } = usePushNotifications();

  const handleBellClick = async () => {
    if (isSupported && !isSubscribed) {
      const promptText = t('push_prompt_msg') || 'Czy chcesz włączyć powiadomienia push, aby nie przegapić ważnych informacji?';
      if (window.confirm(promptText)) {
        await subscribe();
      }
    }
    setDrawerOpen(true);
  };

  const handleLangChange = (newLang: Lang) => {
    if (lang === newLang) { setLangOpen(false); return; }
    setLang(newLang);
    setLangOpen(false);

    // Try to get the URL from PHP-provided map first
    const urls = window.cabinetVars?.cabinetUrls;
    let newUrl = urls?.[newLang];

    // Fallback: build URL from current cabinetUrl using Polylang's URL pattern
    // Polish is the default (no prefix), other languages have /{lang}/ prefix
    if (!newUrl && window.cabinetVars?.cabinetUrl) {
      const base = window.location.origin;
      const current = window.cabinetVars.cabinetUrl.replace(/\/$/, '');
      // Extract the slug part: remove origin and any lang prefix
      const path = current.replace(base, '').replace(/^\/(pl|uk|ru)\//, '/');
      newUrl = newLang === 'pl'
        ? base + path + '/'
        : base + '/' + newLang + path + '/';
    }

    if (newUrl) {
      window.history.replaceState(null, '', newUrl + window.location.search);
    }
  };

  useEffect(() => {
    // Check if app is running in standalone mode (PWA)
    const checkPWA = () => {
      const match = window.matchMedia('(display-mode: standalone)').matches;
      const navStandalone = ('standalone' in navigator) && (navigator as any).standalone;
      setIsPWA(match || navStandalone);
    };
    checkPWA();
    
    // Optional: listen to changes just in case
    window.matchMedia('(display-mode: standalone)').addEventListener('change', checkPWA);
    return () => {
      window.matchMedia('(display-mode: standalone)').removeEventListener('change', checkPWA);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const languages: Lang[] = ['pl', 'uk', 'ru'];

  return (
    <div className="cab-hdr">
      <div className="cab-hdr-inner">
        <a href={lang === 'pl' ? '/' : `/${lang}/`} className="cab-logo">
          <svg width="238" height="36" viewBox="0 0 238 36" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M17.7306 35.4612C27.523 35.4612 35.4612 27.523 35.4612 17.7306C35.4612 7.93827 27.523 0 17.7306 0C7.93827 0 0 7.93827 0 17.7306C0 27.523 7.93827 35.4612 17.7306 35.4612Z" fill="#E6D6FA"/>
<path d="M30.2663 5.19336C37.1906 12.1176 37.1906 23.3441 30.2663 30.2682C23.342 37.1924 12.1156 37.1925 5.19141 30.2682L30.2663 5.19336Z" fill="#D6C1F1"/>
<path d="M5.1942 30.2671C5.59207 30.665 6.00462 31.0389 6.42929 31.3909L31.3929 6.42723C31.0407 6.00267 30.6668 5.59001 30.2691 5.19214C29.8712 4.79427 29.4587 4.42042 29.034 4.06836L4.07031 29.032C4.42248 29.4566 4.79633 29.8693 5.1942 30.2671Z" fill="#BC87FF"/>
<path d="M219.964 31.1971H224.213V34.002H214.459V33.3531C220.194 26.7385 220.948 25.6501 220.948 24.2058C220.948 23.2429 220.299 22.594 219.357 22.594C218.352 22.594 217.724 23.2638 217.724 24.2476V24.5197H214.731V24.2058C214.731 21.6521 216.51 19.81 219.294 19.81C222.036 19.81 223.962 21.6102 223.962 24.1848C223.962 25.8803 223.229 27.4502 219.964 31.1971ZM237.125 28.7271V31.3436H235.932V34.002H233.106V31.3436H226.429V30.7994L231.013 20.0822H233.964L230.28 28.7271H233.19L233.671 25.4198H235.932V28.7271H237.125Z" fill="#BC87FF"/>
<path d="M56.6662 28.8358H59.4083V33.2585H55.7375C50.5186 33.2585 47.2901 30.0299 47.2901 24.7668V13.6215H43V12.6485L51.3147 3.80308H52.1551V9.37572H59.2757V13.6215H52.1993V24.3688C52.1993 27.2436 53.7915 28.8358 56.6662 28.8358Z" fill="#361777"/>
<path d="M80.6064 9.37572H84.3657V33.2585H80.4295L79.943 30.3837C77.7758 32.5508 74.7684 33.8777 71.2744 33.8777C64.1538 33.8777 58.8465 28.4819 58.8465 21.3171C58.8465 14.1523 64.1538 8.80077 71.2744 8.80077C74.8126 8.80077 77.8643 10.1276 80.0314 12.3832L80.6064 9.37572ZM71.6724 29.1453C76.1836 29.1453 79.4565 25.8283 79.4565 21.3171C79.4565 16.8059 76.1836 13.4889 71.6724 13.4889C67.1613 13.4889 63.8442 16.8501 63.8442 21.3171C63.8442 25.7841 67.1613 29.1453 71.6724 29.1453Z" fill="#361777"/>
<path d="M100.831 8.80077C108.084 8.80077 113.259 14.1523 113.259 21.3613C113.259 28.4819 108.084 33.8777 100.831 33.8777C97.514 33.8777 94.5508 32.6393 92.4278 30.5606L91.8087 33.3027H88.0936V0.839844H93.0028V11.5871C95.0815 9.81799 97.7794 8.80077 100.831 8.80077ZM100.477 29.1453C104.988 29.1453 108.261 25.8283 108.261 21.3171C108.261 16.8501 104.988 13.4889 100.477 13.4889C95.966 13.4889 92.649 16.8501 92.649 21.3171C92.649 25.7841 95.966 29.1453 100.477 29.1453Z" fill="#361777"/>
<path d="M115.226 33.2585V0.839844H120.135V33.2585H115.226Z" fill="#361777"/>
<path d="M146.475 21.4056C146.475 21.9363 146.431 22.467 146.342 22.9977H126.971C127.59 26.8897 130.376 29.278 134.268 29.278C137.143 29.278 139.443 27.9512 140.725 25.7398H145.9C143.998 30.7818 139.62 33.8777 134.268 33.8777C127.28 33.8777 122.017 28.4819 122.017 21.3171C122.017 14.1965 127.28 8.80077 134.268 8.80077C141.61 8.80077 146.475 14.4619 146.475 21.4056ZM134.268 13.2677C130.597 13.2677 127.9 15.4791 127.103 19.0173H141.522C140.637 15.3906 137.939 13.2677 134.268 13.2677Z" fill="#361777"/>
<path d="M158.683 28.8358H161.425V33.2585H157.754C152.535 33.2585 149.307 30.0299 149.307 24.7668V13.6215H145.017V12.6485L153.331 3.80308H154.172V9.37572H161.292V13.6215H154.216V24.3688C154.216 27.2436 155.808 28.8358 158.683 28.8358Z" fill="#361777"/>
<path d="M178.415 33.2585L171.25 23.0862H168.287V33.2585H163.378V0.839844H168.287V18.6192H171.295L177.752 9.37572H183.546L175.452 20.8748L184.43 33.2585H178.415Z" fill="#361777"/>
<path d="M203.925 9.37572H207.684V33.2585H203.748L203.261 30.3837C201.094 32.5508 198.087 33.8777 194.593 33.8777C187.472 33.8777 182.165 28.4819 182.165 21.3171C182.165 14.1523 187.472 8.80077 194.593 8.80077C198.131 8.80077 201.183 10.1276 203.35 12.3832L203.925 9.37572ZM194.991 29.1453C199.502 29.1453 202.775 25.8283 202.775 21.3171C202.775 16.8059 199.502 13.4889 194.991 13.4889C190.48 13.4889 187.162 16.8501 187.162 21.3171C187.162 25.7841 190.48 29.1453 194.991 29.1453Z" fill="#361777"/>
</svg>


        </a>
        <div className="cab-hdr-right">
          <div className="cab-lang" ref={langRef}>
            <div className="cab-lang-desktop">
              {languages.map(l => (
                <button 
                  key={l}
                  className={lang === l ? 'active' : ''} 
                  onClick={() => handleLangChange(l)}
                >
                  {l === 'uk' ? 'UA' : l.toUpperCase()}
                </button>
              ))}
            </div>

            <div className={`cab-lang-mobile ${langOpen ? 'open' : ''}`}>
              <button 
                className="active" 
                onClick={() => setLangOpen(!langOpen)}
              >
                {lang === 'uk' ? 'UA' : lang.toUpperCase()}
              </button>
              
              {langOpen && (
                <div className="cab-lang-dropdown">
                  {languages.filter(l => l !== lang).map(l => (
                    <button 
                      key={l}
                      onClick={() => handleLangChange(l)}
                    >
                      {l === 'uk' ? 'UA' : l.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {isAuthenticated && (
            <button className="cab-burger-btn" onClick={logout}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--cab-violet, #361777)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          )}
          {isAuthenticated && isPWA && (
            <button className="cab-bell-btn" data-tour="bell-btn" onClick={handleBellClick}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              {unreadCount > 0 && <span className="cab-bell-badge">{unreadCount}</span>}
            </button>
          )}
        </div>
      </div>
      
      <NotificationDrawer 
        isOpen={drawerOpen} 
        onClose={() => setDrawerOpen(false)} 
        notifications={notifications}
        onMarkRead={markAsRead}
      />
      
      <style>{`
        /* Styles shared with AuthShell but kept scoped here for the cabinet */
        .cab-hdr {
          top: 0;
          z-index: 30;
          padding: 28px 16px 8px;
          width: 100%;
          max-width: 1360px;
          margin: 0 auto;
        }
        .cab-hdr-inner {
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 93px;
          background-color: #f8f7fb;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 100px;
          border: 1px solid rgba(248, 247, 251, 0.5);
          padding: 24px 32px;
          
        }
        .cab-logo {
          display: flex;
          align-items: flex-end;
          gap: 1px;
          font-weight: 800;
          font-size: 19px;
          color: #080A29;
          letter-spacing: -0.02em;
        }
        .cab-leaf {
          width: 20px;
          height: 20px;
          border-radius: 50% 50% 50% 0;
          background: linear-gradient(135deg, var(--cab-bc), var(--cab-d6));
          transform: rotate(45deg);
          margin-right: 6px;
        }
        .cab-s24 {
          color: var(--cab-bc);
          font-size: 12px;
          font-weight: 800;
          margin-left: 1px;
        }
        .cab-hdr-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .cab-lang {
          display: flex;
          position: relative;
        }
        .cab-lang-desktop {
          display: none;
        }
        .cab-lang-mobile {
          display: flex;
          position: relative;
        }
        .cab-lang-mobile button.active {
          width: 40px;
          height: 40px;
          border-radius: 100px;
          background: var(--cab-violet, #361777);
          color: #fff;
          border: none;
          font-weight: 600;
          font-size: 13px;
          cursor: pointer;
        }
        .cab-lang-dropdown {
          position: absolute;
          top: 48px;
          left: 50%;
          transform: translateX(-50%);
          background: #fff;
          border-radius: 20px;
          padding: 12px 0;
          display: flex;
          flex-direction: column;
          gap: 16px;
          box-shadow: 0 4px 20px rgba(54, 23, 119, 0.12);
          z-index: 40;
        }
        .cab-lang-dropdown button {
          background: transparent;
          border: none;
          color: var(--cab-violet, #361777);
          font-weight: 700;
          font-size: 13px;
          cursor: pointer;
          width: 52px;
          height: 20px;
        }
        
        @media (min-width: 1024px) {
          .cab-lang-mobile {
            display: none;
          }
          .cab-lang-desktop {
            display: flex;
            gap: 6px;
          }
          .cab-lang-desktop button {
            width: 44px;
            height: 44px;
            border-radius: 50%;
            font-size: 16px;
            font-weight: 600;
            text-transform: uppercase;
            transition: all 0.3s ease;
            border: 2px solid #9A89B4;
            background-color: transparent;
            color: #9A89B4;
            text-decoration: none;
            cursor: pointer;
            transition: 0.2s;
          }
          .cab-lang-desktop button.active {
            background: var(--cab-violet, #361777);
            color: #fff;
            border-color: var(--cab-violet, #361777);
          }
        }
        .cab-bell-btn {
          width: 44px;
          height: 44px;
          border-radius: 100px;
          background: #fff;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(54, 23, 119, 0.08);
          color: var(--cab-violet, #361777);
          position: relative;
        }
        .cab-bell-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #E84545;
          color: white;
          font-size: 10px;
          font-weight: bold;
          border-radius: 10px;
          min-width: 16px;
          height: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 4px;
        }
        .cab-burger-btn {
          width: 44px;
          height: 44px;
          border-radius: 100px;
          background: #fff;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 8px rgba(54, 23, 119, 0.08);
          color: var(--cab-violet, #361777);
        }

         @media (max-width: 1024px) {
         .cab-hdr {
            padding: 0;
            padding-top: 20px;
          }

          .cab-hdr-inner {
            padding: 8px 24px;
            height: 66px;
          }

           .cab-logo {
          max-width: 160px;
          }

          .cab-burger-btn {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};
